"use client";
import "highlight.js/styles/a11y-dark.css";
import { Header } from "./Header";
import { InputArea } from "./InputArea";
import { MessageItem } from "./MessageItem";
import { type UIMessage } from "@ai-sdk/ui-utils";
import { useComposer } from "../context/ComposerContext";
import { useEffect, useRef, useState } from "react";
import styles from "./Composer.module.css";
// Inner component that uses the context
function ComposerInner({
  onClose,
  fullWidth = false,
}: {
  onClose?: () => void;
  fullWidth?: boolean;
}) {
  const [width, setWidth] = useState(380);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [collapsedReasoning, setCollapsedReasoning] = useState<
    Record<string, boolean>
  >({});
  const [completedReasoning, setCompletedReasoning] = useState<
    Record<string, boolean>
  >({});
  const [collapsedToolResponses, setCollapsedToolResponses] = useState<
    Record<string, boolean>
  >({});
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageContent, setEditingMessageContent] =
    useState<string>("");
  // const resizeRef = useRef<HTMLButtonElement>(null);
  const editingTextareaRef = useRef<HTMLTextAreaElement>(
    null,
  ) as React.RefObject<HTMLTextAreaElement>;
  const [expandedArgs, setExpandedArgs] = useState<Record<string, boolean>>({});

  // Access the enhanced global composer context
  const {
    messages,
    input,
    handleInputChange,
    handleSubmit: handleSubmitContext,
    status,
    stop,
    setMessages,
    resetChat: handleResetChat,
  } = useComposer();

  // Handle submit with context
  const handleSubmit = (
    event:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    // @ts-ignore - need to update the event type
    const sumbission = event?.target?.value || input;
    event.preventDefault();
    handleSubmitContext({
      target: { value: sumbission },
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "l") {
        e.preventDefault();
        if (onClose) onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const newWidth = e.clientX;
      const constrainedWidth = Math.max(280, Math.min(600, newWidth));
      setWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // const startResize = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   setIsDragging(true);
  // };

  // const handleKeyDown = (e: React.KeyboardEvent) => {
  //   if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
  //     e.preventDefault();
  //     const step = 10;
  //     const direction = e.key === "ArrowLeft" ? -1 : 1;
  //     const newWidth = Math.max(280, Math.min(600, width + direction * step));
  //     setWidth(newWidth);
  //   }

  //   if (e.key === "Enter" || e.key === " ") {
  //     e.preventDefault();
  //     setIsDragging(!isDragging);
  //   }

  //   if (e.key === "Escape" && isDragging) {
  //     e.preventDefault();
  //     setIsDragging(false);
  //   }
  // };

  const toggleArgExpansion = (toolCallId: string) => {
    setExpandedArgs((prev) => ({
      ...prev,
      [toolCallId]: !prev[toolCallId],
    }));
  };

  const toggleToolResponseCollapse = (toolCallId: string) => {
    setCollapsedToolResponses((prev) => ({
      ...prev,
      [toolCallId]: !prev[toolCallId],
    }));
  };

  useEffect(() => {
    Object.entries(completedReasoning).forEach(([key, isCompleted]) => {
      if (isCompleted && !collapsedReasoning[key]) {
        const wasManuallyOpened = collapsedReasoning[key] === false;
        if (!wasManuallyOpened) {
          const timeoutId = setTimeout(() => {
            setCollapsedReasoning((prev) => ({
              ...prev,
              [key]: true,
            }));
          }, 1000);
          return () => clearTimeout(timeoutId);
        }
      }
    });
  }, [completedReasoning, collapsedReasoning]);

  const toggleReasoningCollapse = (reasoningId: string) => {
    setCollapsedReasoning((prev) => ({
      ...prev,
      [reasoningId]: !prev[reasoningId],
    }));
  };

  const markReasoningCompleted = (reasoningId: string) => {
    setCompletedReasoning((prev) => ({
      ...prev,
      [reasoningId]: true,
    }));
  };

  // Auto-collapse tool responses by default
  useEffect(() => {
    if (messages.length > 0) {
      const newCollapsedStates: Record<string, boolean> = {};

      messages.forEach((message) => {
        if (message.parts && Array.isArray(message.parts)) {
          message.parts.forEach((part: any) => {
            if (
              part.type === "tool-invocation" &&
              part.toolInvocation &&
              part.toolInvocation.result
            ) {
              // Only auto-collapse if not manually expanded
              if (
                collapsedToolResponses[part.toolInvocation.toolCallId] ===
                undefined
              ) {
                newCollapsedStates[part.toolInvocation.toolCallId] = true;
              }
            }
          });
        }
      });

      if (Object.keys(newCollapsedStates).length > 0) {
        setCollapsedToolResponses((prev) => ({
          ...prev,
          ...newCollapsedStates,
        }));
      }
    }
  }, [messages, collapsedToolResponses]);

  useEffect(() => {
    messages.forEach((message) => {
      if (message.parts && Array.isArray(message.parts)) {
        const hasBothTypes =
          message.parts.some((p: { type: string }) => p.type === "reasoning") &&
          message.parts.some((p: { type: string }) => p.type === "text");

        if (hasBothTypes) {
          message.parts.forEach(
            (part: { type: string; reasoning?: string }) => {
              if (part.type === "reasoning" && part.reasoning) {
                const reasoningId = `${message.id}-${part.reasoning.substring(0, 20)}`;
                if (!completedReasoning[reasoningId]) {
                  markReasoningCompleted(reasoningId);
                }
              }
            },
          );
        }
      }
    });
  }, [messages, completedReasoning]);

  const handleStartEditing = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditingMessageContent(content);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!editingMessageId) return;

      const target = event.target as HTMLElement;
      const editingContainer = editingTextareaRef.current?.closest(
        `.${styles.editingUserMessage}`,
      );

      if (
        editingContainer &&
        !editingContainer.contains(target) &&
        !target.closest(`.${styles.editButtons}`)
      ) {
        handleCancelEditing();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingMessageId]);

  const handleCancelEditing = () => {
    setEditingMessageId(null);
    setEditingMessageContent("");
  };

  const handleSubmitEditedMessage = (
    event:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    if (!editingMessageId) return;
    stop();

    const editIndex = messages.findIndex((msg) => msg.id === editingMessageId);
    if (editIndex === -1) return;

    const messageWithoutLatest = messages.slice(0, editIndex);
    setMessages(messageWithoutLatest);

    setEditingMessageId(null);
    setEditingMessageContent("");
    setErrorMessage(null);

    handleSubmit(event);
  };

  return (
    <div
      className={styles.composer}
      style={{ width: fullWidth ? `100%` : `${width}px` }}
    >
      {/* <button
        className={styles.resizeHandle}
        onMouseDown={startResize}
        onKeyDown={handleKeyDown}
        ref={resizeRef}
        aria-label="Resize chat panel"
      >
        <div className={styles.resizeHandleBar}></div>
      </button> */}

      <Header onClose={onClose} handleResetChat={handleResetChat} />

      {messages.length === 0 && (
        <InputArea
          input={input}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          status={status}
          stop={stop}
        />
      )}

      <div className={styles.messagesContainer}>
        <div className={styles.messagesWrapper}>
          {messages.map((msg, index) => (
            <MessageItem
              key={msg.id || index}
              message={msg as UIMessage}
              index={index}
              editingMessageId={editingMessageId}
              editingMessageContent={editingMessageContent}
              editingTextareaRef={editingTextareaRef}
              handleStartEditing={handleStartEditing}
              handleSubmitEditedMessage={handleSubmitEditedMessage}
              handleCancelEditing={handleCancelEditing}
              handleInputChange={handleInputChange}
              setEditingMessageContent={setEditingMessageContent}
              expandedArgs={expandedArgs}
              toggleArgExpansion={toggleArgExpansion}
              collapsedReasoning={collapsedReasoning}
              completedReasoning={completedReasoning}
              toggleReasoningCollapse={toggleReasoningCollapse}
              collapsedToolResponses={collapsedToolResponses}
              toggleToolResponseCollapse={toggleToolResponseCollapse}
            />
          ))}
        </div>
      </div>

      {(status === "streaming" || status === "submitted") && !errorMessage && (
        <div className={styles.thinkingIndicator}>
          <span className={styles.thinkingText}>{"I'm thinking"}</span>
          <span className={styles.thinkingDots}>...</span>
        </div>
      )}

      {errorMessage && (
        <div className={styles.thinkingIndicator}>
          <span className={styles.thinkingText}>{errorMessage}</span>
          <button
            onClick={handleResetChat}
            className={styles.stopButton}
            style={{ marginLeft: "8px", fontSize: "12px", padding: "4px 8px" }}
          >
            Start Over
          </button>
        </div>
      )}

      {messages.length > 0 && (
        <InputArea
          input={input}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          status={status}
          stop={stop}
        />
      )}
    </div>
  );
}

/**
 * Composer component provides a chat interface for interacting with an AI assistant
 * that can generate and modify canvas elements through ZSL (Zapier System Language).
 *
 * This component includes:
 * - A resizable chat panel
 * - Message history display with support for tool invocations
 * - Input field for user messages
 * - Support for various AI tool interactions (update_ui)
 */
export function Composer({
  onClose,
  additionalData,
  fullWidth = false,
}: {
  onClose?: () => void;
  /**
   * This is the current state of your ui and data.
   * It will be passed to the LLM in the latest user message along with the user's message.
   *
   * This field can be dynamic and does not persist. IE previous user messages will not include this data.
   */
  additionalData?: Record<string, any>;

  /**
   * Whether to use the full width of the container.
   * This is currently used for the support chat, where we want the chat to take up the full width of the container.
   */
  fullWidth?: boolean;
}) {
  const { setAdditionalData } = useComposer();

  // Set additionalData in context when prop changes
  useEffect(() => {
    if (additionalData) {
      setAdditionalData(additionalData);
    }
  }, [additionalData, setAdditionalData]);

  return <ComposerInner onClose={onClose} fullWidth={fullWidth} />;
}

export default Composer;
